/* Importing the necessary libraries and files */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { Form, FormikProvider, useFormik } from 'formik';
import PropTypes from 'prop-types';
import * as Yup from 'yup';

/* Importing the components from the material-ui library. */
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

/* Importing the functions from the Redux slices. */
import { getDevice, putDevice } from '../../features/device/deviceSlice';

/* Importing components from the components folder. */
import AutoCompletePatient from '../../components/AutoCompletePatient';
import PlayNotifSound from '../../components/PlayNotifSound';

/* Importing the functions from the utils folder. */
import fetchAndHandleData from '../../utils/fetchAndHandleData';
import getInitialValues from '../../utils/getInitialValues';
import handleFormResponse from '../../utils/handleFormResponse';
import { fDevice } from '../../utils/Device/Device.utils.format';
import { mDevice } from '../../utils/Device/Device.utils.map';

/* Importing valuesets from the utils folder. */
import { NOTIFICATION_SOUNDS } from '../../utils/monitor/monitor.utils.ValueSets';

/* Defining the propTypes for the MonitorEdit component. */
MonitorEdit.propTypes = {
  deviceID: PropTypes.string,
  isEditOpen: PropTypes.bool,
  setIsEditOpen: PropTypes.func,
};

/**
 * This component is responsible for rendering the edit form for the Monitor section.
 * It uses Formik and Yup for form validation and submission.
 * It also uses Redux for state management.
 *
 * @param {Object} props - The component props.
 * @param {string} props.deviceID - The ID of the device being edited.
 * @param {boolean} props.isEditOpen - A boolean indicating whether the edit form is open or not.
 * @param {function} props.setIsEditOpen - A function to set the state of isEditOpen.
 * @returns {JSX.Element} - The MonitorEdit component.
 */
export default function MonitorEdit(props) {
  const { deviceID, isEditOpen, setIsEditOpen } = props;

  const dispatch = useDispatch();

  const { Devices } = useSelector((state) => state.device);
  const currentDevice = Devices.find((device) => device.id === deviceID);

  const [title, setTitle] = useState('Edit Dashboard');
  const [pageDevice, setPageDevice] = useState({});

  const defaultValues = useMemo(
    () => ({
      notification: false,
      notificationSound: 'default',
      maxslot: 6,
      slot1: '',
      slot2: '',
      slot3: '',
      slot4: '',
      slot5: '',
      slot6: '',
      slot7: '',
      slot8: '',
      slot9: '',
      slot10: '',
    }),
    []
  );

  /* Creating a schema for the form. */
  const DeviceSchema = Yup.object().shape({
    maxslot: Yup.number()
      .min(1, 'Must be at least 1!')
      .max(10, 'Must be less than 10!')
      .required('Maximum number of slots required'),
  });

  /* Using the useFormik hook to create a formik object. */
  const formik = useFormik({
    initialValues: getInitialValues({
      defaultValues,
      data: pageDevice,
    }),
    validationSchema: DeviceSchema,
    onSubmit: (values) => {
      const formValues = {
        ...pageDevice,
        maxslot: values.maxslot,
        notification: values.notification,
        notificationSound: values.notificationSound,
      };

      for (let i = 0; i < 10; i += 1) {
        formValues[`slot${i + 1}`] = '';
        if (i < values.maxslot) {
          if (values[`slot${i + 1}`] && values[`slot${i + 1}`] !== '') {
            formValues[`slot${i + 1}`] = values[`slot${i + 1}`];
          } else {
            formValues[`slot${i + 1}`] = '';
          }
        }
      }

      const deviceData = mDevice(formValues);
      submitPutDevice(deviceData);
    },
  });

  const { errors, touched, handleSubmit, getFieldProps, setFieldValue, setValues, values } = formik;

  useEffect(() => {
    if (!currentDevice) return;
    const deviceObj = fDevice(currentDevice);
    setPageDevice(deviceObj);
    setValues(getInitialValues({ data: deviceObj, defaultValues }));
    setTitle(`Edit Dashboard - ${deviceObj.deviceName_name}`);
  }, [currentDevice, defaultValues, setValues]);

  /* A function that is called when the page loads. */
  const initializeData = useCallback(async () => {
    if (deviceID) {
      await fetchAndHandleData({
        dispatch,
        fetchAction: getDevice,
        id: deviceID,
        errorMessage: 'No Device record found',
        navigateOnFailure: false,
        navigateTo: '',
      });
    }
  }, [dispatch, deviceID]);

  /**
   * It takes the values from the form, adds the deviceID to the values, and then sends the data to the
   * putDevice function.
   */
  const submitPutDevice = async (values) => {
    const data = {
      deviceID,
      data: { ...values, id: deviceID },
    };
    const resultAction = await dispatch(putDevice(data));
    await handleFormResponse({
      resultAction,
      successTitle: 'Device updated successfully!',
      navigatePath: '',
      navigateOnSuccess: false,
      closeDialog: true,
      handleClose,
    });
  };

  const handleClose = () => {
    setIsEditOpen(false);
  };

  useEffect(() => {
    if (values.notificationSound && values.notification) {
      PlayNotifSound(values.notificationSound);
    }
  }, [values.notificationSound, values.notification]);

  /* Calling the initializeData function when the component mounts and when the isEdit state changes. */
  useEffect(() => {
    initializeData();
  }, [initializeData]);

  return (
    <>
      <Dialog open={isEditOpen} onClose={handleClose}>
        <FormikProvider value={formik}>
          <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
            <DialogTitle>{title}</DialogTitle>
            <DialogContent>
              <Grid container item xs={12} columnSpacing={4} rowSpacing={3}>
                <Grid item xs={12}>
                  <Stack spacing={3}>
                    <Stack spacing={2}>
                      <Typography variant="subtitle1" sx={{ pt: 1 }}>
                        Notification
                      </Typography>
                    </Stack>
                    <Stack direction={{ xs: 'column' }} spacing={2}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            name="notification"
                            checked={formik.values.notification}
                            onChange={(e) => {
                              formik.setFieldValue('notification', e.target.checked);
                            }}
                          />
                        }
                        label="Enable notification sound"
                      />
                      <TextField
                        fullWidth
                        select
                        label="Sound"
                        {...getFieldProps('notificationSound')}
                        disabled={!formik.values.notification}
                        error={Boolean(touched.notificationSound && errors.notificationSound)}
                        helperText={touched.notificationSound && errors.notificationSound}
                      >
                        {NOTIFICATION_SOUNDS.map((el) => (
                          <MenuItem key={el.id} value={el.id}>
                            {el.name}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Stack>
                  </Stack>
                </Grid>
                <Grid item xs={12}>
                  <Stack spacing={3}>
                    <Stack spacing={2}>
                      <Typography variant="subtitle1" sx={{ pt: 1 }}>
                        Slots
                      </Typography>
                    </Stack>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                      <TextField
                        fullWidth
                        label="Maximum number of slots"
                        type="number"
                        {...getFieldProps('maxslot')}
                        error={Boolean(touched.maxslot && errors.maxslot)}
                        helperText={touched.maxslot && errors.maxslot}
                      />
                    </Stack>
                  </Stack>
                </Grid>
                <Grid item xs={12}>
                  <Stack spacing={2}>
                    <Typography variant="subtitle1" sx={{ pt: 1 }}>
                      Patients
                    </Typography>
                  </Stack>
                </Grid>

                {[...Array(Math.min(Math.max(formik.values.maxslot, 1), 10))].map((x, i) => (
                  <Grid item xs={12} md={6} key={i}>
                    <Stack spacing={3}>
                      <AutoCompletePatient
                        setFieldValue={setFieldValue}
                        fieldName={`slot${i + 1}`}
                        values={values}
                        touched={touched}
                        errors={errors}
                        patientID={values[`slot${i + 1}`]}
                        label={`Slot ${i + 1}`}
                      />
                    </Stack>
                  </Grid>
                ))}
              </Grid>
            </DialogContent>

            <DialogActions>
              <Button color="error" onClick={handleClose}>
                Cancel
              </Button>
              <Button type="submit">Update</Button>
            </DialogActions>
          </Form>
        </FormikProvider>
      </Dialog>
    </>
  );
}
